import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// `withCredentials: true` lets the browser send/receive the `dentalai_token`
// httpOnly cookie set by the backend. This is our primary auth mechanism —
// JS can no longer read the token, which mitigates token-theft XSS attacks.
export const api = axios.create({ baseURL: API, withCredentials: true });

// Legacy Bearer header — kept ONLY for the transition window and for
// programmatic clients (curl, testing agent) that read `data.token` from
// login/signup responses and pass it back manually. Browsers rely on the
// cookie set by the backend on login.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("dentalai_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// AUTH-401 handling rules:
//   1. NEVER clear auth on a 401 from an arbitrary endpoint — the user may
//      be authenticated but lacks permission for that specific resource,
//      which shouldn't sign them out globally. This was the historical bug:
//      any 401 (from an admin-only endpoint, a plan-gated feature, etc.)
//      nuked the entire session.
//   2. Only clear auth when the backend explicitly says "your token is bad"
//      via the `X-Auth-Reason` header (values: `expired`, `invalid_signature`,
//      `user_not_found`, `invalid_token`, `missing`).
//   3. Also clear on 401 from `/auth/me` (dedicated session-liveness probe).
//   4. Emit a `viyora:auth-expired` DOM event so the AuthProvider can show a
//      friendly "your session ended — please sign in again" toast instead of
//      the raw "Invalid Credentials" error.
const AUTH_FAILURE_REASONS = new Set([
  "expired", "invalid_signature", "invalid_token", "user_not_found", "missing",
]);

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status !== 401) return Promise.reject(err);

    const url = (err.config?.url || "").toString();
    const reason = err.response.headers?.["x-auth-reason"] || null;
    const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/refresh");
    const isAuthFailure = reason && AUTH_FAILURE_REASONS.has(reason);

    // Only invalidate local state when we're SURE it was an auth-level 401.
    if (isAuthProbe || isAuthFailure) {
      localStorage.removeItem("dentalai_token");
      localStorage.removeItem("dentalai_user");
      // Broadcast to the AuthProvider so it can update React state + toast.
      try {
        window.dispatchEvent(new CustomEvent("viyora:auth-expired", {
          detail: { reason: reason || "unknown", url },
        }));
      } catch { /* not a browser */ }
    }
    // Everything else — permission denial, plan gate, etc. — bubbles up so
    // the calling component can `toast.error(apiError(e, "..."))` as usual.
    return Promise.reject(err);
  }
);
